
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, Users } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { authService } from "@/services/auth.service";
import { sessionManager } from "@/utils/sessionManager";

interface HeroSectionProps {
  user: any | null;
  userRole?: string | null;
  loading?: boolean;
  setShowTherapistDialog?: (show: boolean) => void;
  setShowLoginPrompt?: (show: boolean) => void;
  handleTherapistsListClick?: () => void;
  handleJournalClick?: () => void;
  handleTherapistClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  user,
  userRole,
  loading = false,
  setShowTherapistDialog = () => {},
  setShowLoginPrompt = () => {},
  handleTherapistsListClick = () => {},
  handleJournalClick = () => {},
  handleTherapistClick = () => {}
}) => {
  const navigate = useNavigate();

  // Check for admin status separately
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [isCheckingRole, setIsCheckingRole] = React.useState(false);

  useEffect(() => {
    // Check if user is admin when user data changes
    const checkAdminStatus = async () => {
      if (!user) return;
      
      try {
        // Check admin role in database
        const { data: hasAdminRole, error } = await supabase.rpc('has_role', { 
          role_param: 'admin' 
        });
        
        if (!error && hasAdminRole) {
          console.log("Admin status detected");
          setIsAdmin(true);
          return;
        }
        
        // Fallback check for localStorage admin flag
        if (localStorage.getItem('isAdmin') === 'true') {
          setIsAdmin(true);
        }
      } catch (err) {
        console.error("Error checking admin status:", err);
      }
    };
    
    checkAdminStatus();
    
    // Add check for users without roles and silently log them out
    const checkUserRole = async () => {
      if (!user) return;
      
      try {
        setIsCheckingRole(true);
        const hasValidRole = await sessionManager.verifyUserRole(navigate);
        
        if (!hasValidRole && !isAdmin) {
          console.log("User has no role - silent logout initiated");
          await authService.silentSignOut();
          navigate('/auth', { replace: true });
        }
      } catch (err) {
        console.error("Error in role verification:", err);
        // Force logout on error
        await authService.silentSignOut();
        navigate('/auth', { replace: true });
      } finally {
        setIsCheckingRole(false);
      }
    };
    
    checkUserRole();
    
    // Add timeout to force reset loading state
    const timeoutId = setTimeout(() => {
      if (isCheckingRole) {
        setIsCheckingRole(false);
        console.log("Role verification timed out, forcing reset");
        authService.silentSignOut().then(() => {
          navigate('/auth', { replace: true });
        });
      }
    }, 5000);
    
    return () => clearTimeout(timeoutId);
  }, [user, navigate, isAdmin]);

  return <motion.div className="max-w-2xl mx-auto text-center mb-16" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6
  }}>
      <h2 className="text-4xl font-bold text-[#333B44] mb-4">Clou - your Emotional Journal</h2>
      <p className="text-lg text-[#333B44] mb-8">Record and monitor your emotional journey, track your moods, thoughts and emotions in a private, supportive diary designed to enhance your mental well-being.</p>
      
      {loading || isCheckingRole ? <div className="text-[#333B44] italic">Loading your profile...</div> : user ? <div className="flex flex-col sm:flex-row justify-center gap-4">
          {isAdmin ? (
            // Admin-specific UI
            <Button size="lg" className="bg-[#333B44] hover:bg-[#1A6690]" onClick={() => navigate('/admin')}>
              Go to Admin Dashboard
            </Button>
          ) : userRole === 'therapist' ? <>
              <Button size="lg" className="bg-[#333B44] hover:bg-[#1A6690]" onClick={handleTherapistClick}>
                <Users className="mr-2 h-5 w-5" /> Go to Therapist Dashboard
              </Button>
              <Button size="lg" variant="outline" onClick={handleTherapistsListClick}>
                <Users className="mr-2 h-5 w-5" /> Therapist Directory
              </Button>
            </> : userRole === 'client' ? <>
              <Button size="lg" className="bg-[#333B44] hover:bg-[#1A6690]" onClick={handleJournalClick}>
                <BookOpen className="mr-2 h-5 w-5" /> Open Your Journal
              </Button>
              <Button size="lg" variant="outline" onClick={handleTherapistsListClick}>
                <Users className="mr-2 h-5 w-5" /> Therapist Directory
              </Button>
            </> : <div className="flex justify-center">
              <Button 
                size="lg" 
                className="bg-red-500 hover:bg-red-600 text-white" 
                onClick={async () => {
                  await authService.silentSignOut();
                  navigate('/auth', { replace: true });
                }}
              >
                Session Error - Click to Restart
              </Button>
            </div>}
        </div> : <div className="text-[#333B44] text-lg mb-6">
          <p className="mb-6">Log in to start your journey or explore the therapists on Clou.</p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button size="lg" className="bg-[#333B44] hover:bg-[#1A6690]" onClick={() => {
              console.log("START JOURNALING button clicked, navigating to client login");
              navigate('/client-login');
            }}>
              <BookOpen className="mr-2 h-5 w-5" /> START JOURNALING
            </Button>
            <Button size="lg" variant="outline" onClick={() => {
              console.log("THERAPIST DIRECTORY button clicked, navigating to therapist directory");
              localStorage.setItem('redirectAfterLogin', '/therapists');
              navigate('/therapists');
            }}>
              <Users className="mr-2 h-5 w-5" /> THERAPIST DIRECTORY
            </Button>
          </div>
        </div>}
    </motion.div>;
};

export default HeroSection;
